<template>
  <div class="three-viewer" ref="viewerContainer">
    <div v-if="loading" class="loading-overlay">
      <div class="spinner"></div>
      <p>加载模型中... {{ Math.round(progress) }}%</p>
    </div>
    <div v-if="error" class="error-overlay">
      <p>{{ error }}</p>
    </div>
    <div class="viewer-controls" v-if="!loading && !error">
      <button class="control-btn" @click="zoomIn" title="放大">
        <i class="fas fa-plus"></i>
      </button>
      <button class="control-btn" @click="zoomOut" title="缩小">
        <i class="fas fa-minus"></i>
      </button>
      <button class="control-btn" @click="captureScreenshot" title="下载当前视角截图">
        <i class="fas fa-camera"></i>
      </button>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';

export default {
  name: 'ThreeViewer',
  props: {
    modelUrl: {
      type: String,
      required: true
    },
    authToken: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: true,
      progress: 0,
      error: null,
      blobUrl: null
    };
  },
  watch: {
    modelUrl: {
      handler(newVal) {
        if (newVal) {
          this.loadModel();
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.initScene();
    window.addEventListener('resize', this.onWindowResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
    this.disposeScene();
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
    }
  },
  methods: {
    initScene() {
      const container = this.$refs.viewerContainer;

      // Scene
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0x2c3e50);

      // Camera
      this.camera = new THREE.PerspectiveCamera(45, container.clientWidth / container.clientHeight, 0.1, 100);
      this.camera.position.set(3, 2, 3);

      // Renderer
      this.renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.renderer.setSize(container.clientWidth, container.clientHeight);
      this.renderer.outputEncoding = THREE.sRGBEncoding;
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1;
      container.appendChild(this.renderer.domElement);

      // Environment
      const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
      this.scene.environment = pmremGenerator.fromScene(new RoomEnvironment(), 0.04).texture;

      // Controls
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;
      this.controls.minDistance = 1;
      this.controls.maxDistance = 10;
      this.controls.target.set(0, 0, 0);

      // Animation Loop
      this.animate();
    },
    async loadModel() {
      if (!this.modelUrl) return;

      this.loading = true;
      this.progress = 0;
      this.error = null;

      // Clear previous model
      if (this.currentModel) {
        this.scene.remove(this.currentModel);
        this.currentModel = null;
      }

      // Cleanup previous blob url
      if (this.blobUrl) {
        URL.revokeObjectURL(this.blobUrl);
        this.blobUrl = null;
      }

      let urlToLoad = this.modelUrl;
      let isFbx = this.modelUrl.toLowerCase().includes('.fbx');

      // If auth token is provided, fetch with headers and create blob url
      if (this.authToken) {
        try {
          const response = await fetch(this.modelUrl, {
            headers: {
              'x-access-token': this.authToken
            }
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const blob = await response.blob();

          // Detect file type by magic numbers
          const buffer = await blob.slice(0, 20).arrayBuffer();
          const arr = new Uint8Array(buffer);

          // Check for FBX magic number: "Kaydara FBX Binary"
          const fbxMagic = [0x4B, 0x61, 0x79, 0x64, 0x61, 0x72, 0x61, 0x20, 0x46, 0x42, 0x58, 0x20, 0x42, 0x69, 0x6E, 0x61, 0x72, 0x79, 0x20, 0x20];
          let isFbxBinary = true;
          for (let i = 0; i < fbxMagic.length; i++) {
            if (arr[i] !== fbxMagic[i]) {
              isFbxBinary = false;
              break;
            }
          }

          if (isFbxBinary) {
            isFbx = true;
          } else {
            // Also check for text FBX (starts with "; FBX")
            const text = await blob.slice(0, 10).text();
            if (text.startsWith('; FBX')) {
              isFbx = true;
            }
          }

          this.blobUrl = URL.createObjectURL(blob);
          urlToLoad = this.blobUrl;
        } catch (err) {
          console.error('Error fetching model with auth:', err);
          this.error = '模型下载失败，请检查权限';
          this.loading = false;
          return;
        }
      }

      const onLoad = (object) => {
        this.currentModel = object.scene || object;

        // Center and scale model
        const box = new THREE.Box3().setFromObject(this.currentModel);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2 / maxDim;
        this.currentModel.scale.setScalar(scale);

        // Re-center after scaling
        box.setFromObject(this.currentModel);
        box.getCenter(center);
        this.currentModel.position.sub(center); // Move model center to origin

        this.scene.add(this.currentModel);

        this.loading = false;
      };

      const onProgress = (xhr) => {
        if (xhr.lengthComputable) {
          this.progress = (xhr.loaded / xhr.total) * 100;
        }
      };

      const onError = (error) => {
        console.error('An error happened loading the model:', error);
        this.error = '模型加载失败，请检查网络或链接是否有效';
        this.loading = false;
      };

      if (isFbx) {
        const loader = new FBXLoader();
        loader.load(urlToLoad, onLoad, onProgress, onError);
      } else {
        const loader = new GLTFLoader();
        loader.load(urlToLoad, onLoad, onProgress, onError);
      }
    },
    onWindowResize() {
      if (!this.camera || !this.renderer) return;
      const container = this.$refs.viewerContainer;
      if (!container) return;

      this.camera.aspect = container.clientWidth / container.clientHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(container.clientWidth, container.clientHeight);
    },
    animate() {
      this.animationId = requestAnimationFrame(this.animate);
      if (this.controls) this.controls.update();
      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
    },
    disposeScene() {
      cancelAnimationFrame(this.animationId);

      if (this.renderer) {
        this.renderer.dispose();
        const container = this.$refs.viewerContainer;
        if (container && this.renderer.domElement) {
          container.removeChild(this.renderer.domElement);
        }
      }

      if (this.currentModel) {
        this.scene.remove(this.currentModel);
        this.currentModel.traverse((child) => {
          if (child.isMesh) {
            child.geometry.dispose();
            if (child.material.isMaterial) {
              child.material.dispose();
            }
          }
        });
      }
    },
    zoomIn() {
      if (!this.controls || !this.camera) return;
      const distance = this.camera.position.distanceTo(this.controls.target);
      const newDistance = Math.max(distance * 0.8, this.controls.minDistance);

      const direction = new THREE.Vector3().subVectors(this.camera.position, this.controls.target).normalize();
      this.camera.position.copy(this.controls.target).add(direction.multiplyScalar(newDistance));
      this.controls.update();
    },
    zoomOut() {
      if (!this.controls || !this.camera) return;
      const distance = this.camera.position.distanceTo(this.controls.target);
      const newDistance = Math.min(distance * 1.2, this.controls.maxDistance);

      const direction = new THREE.Vector3().subVectors(this.camera.position, this.controls.target).normalize();
      this.camera.position.copy(this.controls.target).add(direction.multiplyScalar(newDistance));
      this.controls.update();
    },
    captureScreenshot() {
      if (!this.renderer || !this.scene || !this.camera) return;

      this.renderer.render(this.scene, this.camera);
      const dataUrl = this.renderer.domElement.toDataURL('image/png');

      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `3d-screenshot-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};
</script>

<style scoped>
.three-viewer {
  width: 100%;
  height: 100%;
  position: relative;
  background: #f5f5f5;
  overflow: hidden;
  border-radius: 8px;
}

.loading-overlay, .error-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-overlay {
  color: #e74c3c;
  padding: 20px;
  text-align: center;
}

.viewer-controls {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.control-btn {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #ddd;
  border-radius: 6px;
  width: 36px;
  height: 36px;
  padding: 0;
  justify-content: center;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 16px;
  color: #333;
  transition: all 0.2s;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
}

.control-btn:hover {
  background: white;
  color: #2196F3;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}
</style>
